/**
 * **Produk** — halamannya sendiri (Imron, 2026-08-02).
 *
 * ⭐ Dipisah dari struktur (departemen & kategori) karena keduanya dikerjakan pada waktu berbeda:
 * struktur ditata sekali saat onboarding; produk, harga, dan flag-nya disunting tiap minggu.
 * Halaman gabungan memaksa yang sering dipakai melewati yang jarang diubah.
 *
 * ⚠️ Penyaringnya tetap berurut — brand → departemen → kategori → produk. Hierarki tetap
 * disiplin; yang berubah cuma halamannya, bukan aturannya.
 *
 * ### ⚠️ Bedanya dari layar Struktur & Promo, dan kenapa disengaja
 * Di sini klik baris **MEMILIH**, bukan membuka laci. Produk membawa jauh lebih banyak isi
 * daripada yang muat di satu laci sempit — nama struk, tiga flag kontrol, ketersediaan berjadwal,
 * barcode, dan riwayat harga. Menjejalkan semua ke laci membuat orang menggulir mencari satu
 * sakelar. ⇒ Laci di layar ini hanya untuk **membuat**; menyuntingnya terjadi di kartu detail di
 * bawah tabel, tempat semuanya terbaca sekaligus.
 */
import {
  Alert, Button, Card, DatePicker, Empty, Form, Input, Select, Space, Table, Tabs, Tag, message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { Category, Department, Price, Product, TaxGroup } from '../api/client';
import { canWrite } from '../api/session';
import { BrandPicker } from '../components/BrandPicker';
import { CrudPanel } from '../components/CrudPanel';
import { ProductBarcodes } from './ProductBarcodes';
import { ProductDetail } from './ProductDetail';

/** Kanal harga — enum kontrak. ⛔ Bukan teks bebas: kanal karangan = harga yang tak pernah terpakai. */
const CHANNELS = [
  'dine_in', 'takeaway', 'delivery_gofood', 'delivery_grab', 'delivery_shopee', 'kiosk',
] as const;

function rupiah(kanonik: string): string {
  const bulat = kanonik.split('.')[0] ?? '0';
  return `Rp ${bulat.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
}

export function Products() {
  const { t } = useTranslation();
  const boleh = canWrite();

  const [brandId, setBrandId] = useState<string | null>(null);
  const [department, setDepartment] = useState<readonly Department[]>([]);
  const [category, setCategory] = useState<readonly Category[]>([]);
  const [price, setPrice] = useState<readonly Price[]>([]);
  const [depId, setDepId] = useState<string | null>(null);
  const [katId, setKatId] = useState<string | null>(null);
  const [prod, setProd] = useState<Product | null>(null);
  const [busy, setBusy] = useState(false);
  /** Dinaikkan saat kartu detail menyimpan — nama produk ikut tampil di kolom tabel. */
  const [tick, setTick] = useState(0);
  const [grupPajak, setGrupPajak] = useState<readonly TaxGroup[]>([]);

  useEffect(() => { void api.taxGroups().then(setGrupPajak).catch(() => undefined); }, []);


  useEffect(() => {
    if (brandId === null) return;
    void api.departments(brandId).then(setDepartment).catch(() => undefined);
  }, [brandId]);
  useEffect(() => {
    if (depId === null) { setCategory([]); return; }
    void api.categories(depId).then(setCategory).catch(() => undefined);
  }, [depId]);

  /* ⛔ `includeInactive`: layar pengelola — produk yang dimatikan harus tetap bisa dinyalakan. */
  const muatProduk = useCallback(async (): Promise<readonly Product[]> => {
    if (katId === null) return [];
    return api.products(katId, true);
  }, [katId]);

  const muatHarga = useCallback(() => {
    if (prod === null) { setPrice([]); return; }
    void api.prices(prod.id).then(setPrice).catch(() => undefined);
  }, [prod]);
  useEffect(() => { muatHarga(); }, [muatHarga]);

  /*
   * ⚠️ `tambahHarga` DIBUANG dari sini bersama formulirnya (2026-08-04) — pengisian harga kini
   * tinggal di halaman Harga, satu tempat untuk semua sasaran & kanal. Yang tersisa di laci ini
   * hanya PEMBACAAN riwayat.
   */

  const kolom: ColumnsType<Product> = [
    {
      title: t('master.code'),
      dataIndex: 'code',
      width: 150,
      render: (v: string) => <span data-testid={`prod-${v}`}>{v}</span>,
    },
    { title: t('master.name'), dataIndex: 'name' },
    {
      title: t('products.kind'),
      dataIndex: 'product_kind',
      width: 110,
      render: (v: string) => (v === 'combo' ? <Tag color="blue">{v}</Tag> : v),
    },
    {
      title: '',
      dataIndex: 'is_active',
      width: 96,
      render: (v: boolean) =>
        v === false ? <Tag color="red">{t('outlets.inactive')}</Tag> : <Tag color="green">{t('outlets.active')}</Tag>,
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }} data-testid="products">
      <Space wrap>
        <BrandPicker value={brandId} onChange={(id) => {
          setBrandId(id); setDepId(null); setKatId(null); setProd(null);
        }} />
        <span data-testid="pilih-dep">
          <Select
            style={{ width: 200 }}
            placeholder={t('master.departments')}
            value={depId}
            onChange={(v: string) => { setDepId(v); setKatId(null); setProd(null); }}
            options={department.map((d) => ({ value: d.id, label: `${d.code} · ${d.name}` }))}
          />
        </span>
        <span data-testid="pilih-kat">
          <Select
            style={{ width: 200 }}
            placeholder={t('master.categories')}
            value={katId}
            disabled={depId === null}
            onChange={(v: string) => { setKatId(v); setProd(null); }}
            options={category.map((c) => ({ value: c.id, label: `${c.code} · ${c.name}` }))}
          />
        </span>
      </Space>

      {katId === null ? (
        <Empty description={t('master.pickCategory')} data-testid="prod-pilih-dulu" />
      ) : (
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <CrudPanel<Product>
            testId="prod"
        historyPath="products"
            title={t('master.products')}
            subtitle={t('products.subtitle')}
            canWrite={boleh}
            newLabel={t('products.new')}
            columns={kolom}
            fetchList={muatProduk}
            searchIn={(p) => `${p.code} ${p.name}`}
            emptyText={t('products.empty')}
            /* ⚠️ Klik baris MEMILIH — sunting terjadi di kartu detail, bukan di laci. */
            onSelect={setProd}
            selectedId={prod?.id ?? null}
            reloadToken={tick}
            renderFields={() => (
              <>
                <Form.Item
                  name="code"
                  label={t('master.code')}
                  rules={[{ required: true, pattern: /^[A-Za-z0-9_-]+$/, message: t('structure.codeRule') }]}
                  tooltip={t('structure.codeFixed')}
                >
                  <Input maxLength={40} data-testid="prod-kode" />
                </Form.Item>
                <Form.Item name="name" label={t('master.name')} rules={[{ required: true, min: 2 }]}>
                  <Input maxLength={120} data-testid="prod-nama" />
                </Form.Item>
                {/*
                  * ⭐⭐ Kelas fiskal WAJIB — dan bawaannya STANDARD, TERLIHAT, bukan tersirat.
                  * Rokok & voucher dipindah ke grupnya sendiri di sini, sekali, saat produknya
                  * lahir. Kalau pilihan ini disembunyikan, yang terjadi adalah rokok kena service
                  * charge sampai ada tamu yang bertanya.
                  */}
                <Form.Item
                  name="taxGroupId"
                  label={t('tax.group')}
                  rules={[{ required: true }]}
                  tooltip={t('tax.groupHint')}
                  initialValue={grupPajak.find((g) => g.code === 'STANDARD')?.id}
                >
                  <Select
                    options={grupPajak.map((g) => ({ value: g.id, label: `${g.code} · ${g.name}` }))}
                  />
                </Form.Item>
                {/*
                  ⭐⭐⭐ **Jenis produk — hanya bisa disetel saat LAHIR** (`product_kind` beku).
                  ⛔ Sampai 2026-08-06 pilihan ini tak ada di mana pun, jadi produk `combo` cuma
                  bisa dibuat lewat SQL — dan penyusun paketnya (`ProductCombo`, yang muncul hanya
                  untuk `product_kind = 'combo'`) tak pernah bisa dijangkau siapa pun. Setengah
                  fitur yang terkirim.
                  ⚠️ Bekunya disengaja: mengubah produk biasa jadi combo membuat baris pesanan LAMA
                  tak konsisten dengan masternya — struk lama menunjukkan satu item, masternya
                  menyatakan paket. Karena itu ia ditanyakan SEKARANG, bukan nanti.
                */}
                <Form.Item
                  name="productKind"
                  label={t('products.kind')}
                  initialValue="simple"
                  tooltip={t('products.kindHint')}
                >
                  <Select
                    data-testid="prod-jenis"
                    options={[
                      { value: 'simple', label: t('products.kindSimple') },
                      { value: 'combo', label: t('products.kindCombo') },
                    ]}
                  />
                </Form.Item>
                {/* ⚠️ Kategorinya sudah terpilih di penyaring — disebut lagi supaya yang menyimpan
                    tahu produk ini mendarat di mana, bukan menebak dari layar sebelah. */}
                <Alert
                  type="info"
                  showIcon
                  title={`${t('master.categories')}: ${
                    category.find((c) => c.id === katId)?.name ?? ''
                  }`}
                />
              </>
            )}
            onCreate={async (v) => {
              if (katId === null) return;
              const baru = await api.createProduct({
                code: String(v['code']).toUpperCase(),
                name: String(v['name']),
                categoryId: katId,
                taxGroupId: String(v['taxGroupId']),
                productKind: v['productKind'] === 'combo' ? 'combo' : 'simple',
              });
              /* ⭐ Langsung terpilih: yang baru dibuat selalu perlu harga & rute, dan keduanya ada
                 di kartu di bawah. Membuatnya lalu membiarkan layar diam membuat orang mengira
                 belum jadi. */
              setProd(baru);
            }}
          />

          {prod !== null && (
            /*
             * ⭐ Tab di tingkat LAYAR, bukan di dalam formulir (Imron, 2026-08-03).
             *
             * Detail, barcode, dan harga masing-masing punya FORMULIRNYA SENDIRI — dan formulir di
             * dalam formulir tak sah. Karena itu ketiganya jadi tab di sini, bukan `FormTabs`.
             * ⚠️ Isi tab yang belum dibuka memang belum di-mount di sini, dan itu TAK apa: tak ada
             * satu pun isian wajib yang tersembunyi di dalamnya — tiap tab menyimpan sendiri.
             */
            <Tabs
              defaultActiveKey="detail"
              items={[
                {
                  key: 'detail',
                  label: <span data-testid="produk-tab-detail">{t('product.detail')}</span>,
                  children: (
                    <ProductDetail
                      product={prod}
                      onSaved={(baru) => { setProd(baru); setTick((n) => n + 1); }}
                    />
                  ),
                },
                {
                  key: 'barcode',
                  label: <span data-testid="produk-tab-barcode">{t('product.barcodes')}</span>,
                  children: <ProductBarcodes product={prod} />,
                },
                {
                  key: 'harga',
                  label: <span data-testid="produk-tab-harga">{t('master.prices')}</span>,
                  children: (
              <Card size="small" title={`${t('master.prices')} · ${prod.code}`} data-testid="kolom-price">
                      <Table
                        size="small"
                        rowKey="id"
                        pagination={false}
                        dataSource={[...price]}
                        locale={{ emptyText: <span data-testid="harga-kosong">{t('master.noPrice')}</span> }}
                        columns={[
                          {
                            title: t('master.channel'),
                            dataIndex: 'channel',
                            render: (v: string, r: Price) => (
                              <span data-testid={`harga-${v}-${r.valid_from}`}>{v}</span>
                            ),
                          },
                          { title: t('master.price'), dataIndex: 'price', render: (v: string) => rupiah(v) },
                          { title: t('master.validFrom'), dataIndex: 'valid_from' },
                          {
                            title: t('master.validTo'),
                            dataIndex: 'valid_to',
                            render: (v: string | null, r: Price) =>
                              v ?? (
                                <Tag color="green" data-testid={`harga-berjalan-${r.channel}`}>
                                  {t('master.running')}
                                </Tag>
                              ),
                          },
                        ]}
                      />

                      {/*
                        ⛔⛔ **Form pengisian harga DIPINDAH ke halaman Harga** (Imron, 2026-08-04:
                        *"pembuatan harga di price group saja, karena butuh masukkan harga ke banyak
                        channel"*).

                        Yang sebenarnya dikerjakan orang saat menyiapkan menu adalah *satu sasaran,
                        banyak produk, beberapa kanal sekaligus* — dan lewat laci ini itu berarti
                        300 laci × 6 kanal. Laci produk tetap menjawab pertanyaan yang memang
                        miliknya: *"berapa harga produk INI, di kanal apa, sejak kapan"*.

                        ⚠️ Riwayatnya SENGAJA tetap di sini dan tetap terbaca penuh. Memindahkan
                        pengisian bukan alasan menyembunyikan sejarah harga — pertanyaan "berapa
                        harganya bulan lalu?" datang saat seseorang menatap SATU produk, bukan saat
                        ia sedang menyusun daftar harga.
                      */}
                      <Alert type="info" showIcon style={{ marginTop: 12 }} title={t('master.priceRule')}
                             data-testid="harga-catatan" />
                      {/* ⭐ Menyebut KE MANA harus pergi. Layar yang cuma menghilangkan tombolnya
                          membuat orang menyimpulkan fiturnya rusak, lalu mencarinya di SQL. */}
                      <Alert type="warning" showIcon style={{ marginTop: 8 }}
                             title={t('master.priceMovedHint')}
                             data-testid="harga-pindah" />
                    </Card>
                  ),
                },
              ]}
            />
          )}
        </Space>
      )}
    </Space>
  );
}
